import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { humanise } from '../lib/format';
import { Badge, Button, Card, CardHead, Empty, Field, Input, Modal, Notice, Select, Textarea, useToast } from '../components/ui';

interface CategoryNode {
  id: string;
  name: string;
  path: string;
  children?: CategoryNode[];
}
interface FilterDef {
  id: string;
  key: string;
  label: string;
  kind: string;
  unit: string | null;
  minValue: number | null;
  maxValue: number | null;
  step: number | null;
  sortOrder: number;
  isActive: boolean;
  inherited: boolean;
  options: Array<{ id: string; value: string; label: string; sortOrder: number }>;
}

const KINDS = ['CHOICE', 'MULTI_CHOICE', 'RANGE', 'BOOLEAN', 'TEXT'];

/**
 * v2 §9/§34/§38.
 *
 * This screen is what makes the app's filter sheet data-driven. The response
 * shape it edits is exactly what the Flutter FilterSheet renders, so adding
 * "Boot space" to Cars is an admin action, not a release.
 */
export function FiltersPage() {
  const [categoryId, setCategoryId] = useState('');
  const [editing, setEditing] = useState<{ row?: FilterDef } | null>(null);
  const toast = useToast();
  const qc = useQueryClient();

  const categories = useQuery({
    queryKey: ['categories-tree'],
    queryFn: () => api.get<CategoryNode[]>('/categories?tree=true'),
  });

  const filters = useQuery({
    queryKey: ['filters-admin', categoryId],
    queryFn: () => api.get<FilterDef[]>(`/filters/admin?categoryId=${categoryId}`),
    enabled: Boolean(categoryId),
  });

  const effective = useQuery({
    queryKey: ['filters-effective', categoryId],
    queryFn: () => api.get<Array<{ key: string; label: string; kind: string }>>(`/filters?categoryId=${categoryId}`),
    enabled: Boolean(categoryId),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['filters-admin', categoryId] });
    qc.invalidateQueries({ queryKey: ['filters-effective', categoryId] });
  };

  const save = useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
      id ? api.patch(`/filters/${id}`, body) : api.post('/filters', body),
    onSuccess: () => {
      toast('Filter saved', 'success');
      setEditing(null);
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/filters/${id}`),
    onSuccess: () => {
      toast('Filter removed', 'success');
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const flatten = (nodes: CategoryNode[], depth = 0): Array<{ value: string; label: string }> =>
    nodes.flatMap((n) => [
      { value: n.id, label: `${'— '.repeat(depth)}${n.name}` },
      ...(n.children?.length ? flatten(n.children, depth + 1) : []),
    ]);

  const own = new Set((filters.data ?? []).map((f) => f.key));
  const inheritedOnly = (effective.data ?? []).filter((f) => !own.has(f.key));

  return (
    <div className="stack">
      <Notice tone="info" title="These are the filters the app renders">
        The app's filter sheet switches on the field type alone, so anything defined here appears without an app
        update. Mark a filter “inherited” to have every descendant category pick it up — that is how Price is defined
        once rather than ninety times.
      </Notice>

      <div className="filters">
        <Field label="Category">
          <Select
            value={categoryId}
            placeholder="Choose a category…"
            onChange={(e) => setCategoryId(e.target.value)}
            options={categories.data ? flatten(categories.data) : []}
          />
        </Field>
        {categoryId && (
          <div style={{ alignSelf: 'flex-end' }}>
            <Button variant="primary" onClick={() => setEditing({})}>
              + New filter
            </Button>
          </div>
        )}
      </div>

      {!categoryId ? (
        <Card>
          <Empty icon="⚙" title="Pick a category" body="Filters are defined per category, and inherited down the tree." />
        </Card>
      ) : (
        <>
          <Card flush>
            <CardHead title="Defined on this category" />
            {filters.isLoading ? (
              <div style={{ padding: 'var(--s5)' }}>
                <div className="skeleton" style={{ height: 100 }} />
              </div>
            ) : !filters.data?.length ? (
              <Empty icon="⚙" title="No filters defined here" body="This category still inherits anything set on an ancestor." />
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Key</th>
                      <th>Label</th>
                      <th>Type</th>
                      <th>Range / options</th>
                      <th>Inherited</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {filters.data.map((f) => (
                      <tr key={f.id}>
                        <td className="cell-mono">{f.key}</td>
                        <td className="cell-strong">{f.label}</td>
                        <td>
                          <Badge tone="blue">{humanise(f.kind)}</Badge>
                        </td>
                        <td className="cell-muted">
                          {f.kind === 'RANGE'
                            ? `${f.minValue} – ${f.maxValue}${f.unit ? ` ${f.unit}` : ''}`
                            : f.options.length
                              ? `${f.options.length} options`
                              : '—'}
                        </td>
                        <td>{f.inherited ? <Badge tone="purple">Descendants too</Badge> : <span className="cell-muted">—</span>}</td>
                        <td>
                          <div className="cell-actions">
                            <Button size="sm" onClick={() => setEditing({ row: f })}>
                              Edit
                            </Button>
                            <Button size="sm" variant="danger" onClick={() => remove.mutate(f.id)}>
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {inheritedOnly.length > 0 && (
            <Card>
              <CardHead title="Inherited from an ancestor" sub="Shown to users on this category, edited where they are defined" />
              <div className="row row--wrap" style={{ gap: 7, paddingTop: 'var(--s4)' }}>
                {inheritedOnly.map((f) => (
                  <Badge key={f.key} tone="neutral">
                    {f.label} · {humanise(f.kind)}
                  </Badge>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {editing && (
        <FilterEditor
          row={editing.row}
          categoryId={categoryId}
          saving={save.isPending}
          onClose={() => setEditing(null)}
          onSave={(body) => save.mutate({ id: editing.row?.id, body })}
        />
      )}
    </div>
  );
}

function FilterEditor({
  row,
  categoryId,
  onClose,
  onSave,
  saving,
}: {
  row?: FilterDef;
  categoryId: string;
  onClose: () => void;
  onSave: (body: Record<string, unknown>) => void;
  saving: boolean;
}) {
  const [form, setForm] = useState({
    key: row?.key ?? '',
    label: row?.label ?? '',
    kind: row?.kind ?? 'CHOICE',
    unit: row?.unit ?? '',
    minValue: row?.minValue != null ? String(row.minValue) : '',
    maxValue: row?.maxValue != null ? String(row.maxValue) : '',
    step: row?.step != null ? String(row.step) : '',
    sortOrder: String(row?.sortOrder ?? 0),
    inherited: row?.inherited ? 'true' : 'false',
    options: (row?.options ?? []).map((o) => o.label).join('\n'),
  });
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const needsOptions = form.kind === 'CHOICE' || form.kind === 'MULTI_CHOICE';
  const needsRange = form.kind === 'RANGE';

  function submit() {
    const body: Record<string, unknown> = {
      label: form.label.trim(),
      unit: form.unit.trim() || undefined,
      sortOrder: Number(form.sortOrder) || 0,
      inherited: form.inherited === 'true',
    };
    if (!row) {
      body.categoryId = categoryId;
      body.key = form.key.trim();
      body.kind = form.kind;
    }
    if (needsRange) {
      body.minValue = Number(form.minValue);
      body.maxValue = Number(form.maxValue);
      if (form.step) body.step = Number(form.step);
    }
    if (needsOptions) {
      body.options = form.options
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
        .map((label, i) => ({
          // The stored value is a slug so it survives a label being reworded.
          value: label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
          label,
          sortOrder: i,
        }));
    }
    onSave(body);
  }

  const valid =
    form.label.trim() &&
    (row || form.key.trim()) &&
    (!needsRange || (form.minValue !== '' && form.maxValue !== '' && Number(form.minValue) < Number(form.maxValue))) &&
    (!needsOptions || form.options.trim());

  return (
    <Modal
      title={row ? `Edit “${row.label}”` : 'New filter'}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} disabled={!valid} onClick={submit}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="row">
          <Field label="Key" hint="Matches the attribute stored on a listing.">
            <Input value={form.key} disabled={Boolean(row)} onChange={(e) => set('key', e.target.value)} placeholder="brand" />
          </Field>
          <Field label="Label" hint="What the user sees.">
            <Input value={form.label} onChange={(e) => set('label', e.target.value)} placeholder="Brand" />
          </Field>
        </div>

        <div className="row">
          <Field label="Type">
            <Select
              value={form.kind}
              disabled={Boolean(row)}
              onChange={(e) => set('kind', e.target.value)}
              options={KINDS.map((k) => ({ value: k, label: humanise(k) }))}
            />
          </Field>
          <Field label="Sort order">
            <Input type="number" value={form.sortOrder} onChange={(e) => set('sortOrder', e.target.value)} />
          </Field>
          <Field label="Applies to descendants">
            <Select
              value={form.inherited}
              onChange={(e) => set('inherited', e.target.value)}
              options={[
                { value: 'false', label: 'This category only' },
                { value: 'true', label: 'This and all below' },
              ]}
            />
          </Field>
        </div>

        {needsRange && (
          <div className="row">
            <Field label="Minimum">
              <Input type="number" value={form.minValue} onChange={(e) => set('minValue', e.target.value)} />
            </Field>
            <Field label="Maximum">
              <Input type="number" value={form.maxValue} onChange={(e) => set('maxValue', e.target.value)} />
            </Field>
            <Field label="Step">
              <Input type="number" value={form.step} onChange={(e) => set('step', e.target.value)} />
            </Field>
            <Field label="Unit">
              <Input value={form.unit} onChange={(e) => set('unit', e.target.value)} placeholder="km" />
            </Field>
          </div>
        )}

        {needsOptions && (
          <Field label="Options" hint="One per line. Editing this replaces the whole list.">
            <Textarea
              value={form.options}
              onChange={(e) => set('options', e.target.value)}
              placeholder={'Maruti Suzuki\nHyundai\nTata'}
              style={{ minHeight: 150 }}
            />
          </Field>
        )}
      </div>
    </Modal>
  );
}
